import clsx from "clsx";
import type { ForwardedRef } from "react";
import React, { forwardRef, useCallback } from "react";
// Direct sibling imports (not the "@appsmith/wds" barrel) to avoid adding a
// new package-barrel cyclic dependency — see ci-client-cyclic-deps-check.
import { Button } from "../../Button";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import { DropZone, FileTrigger } from "react-aria-components";
import type { DropEvent, FileDropItem } from "@react-types/shared";

import styles from "./styles.module.css";
import type { FileUploadProps } from "./types";

/**
 * FileUpload composes react-aria-components' `FileTrigger` and `DropZone`:
 *
 * - `button` mode renders only the trigger button (a real `<button>` wired to
 *   a hidden native `<input type="file">`).
 * - `dropzone` mode wraps the trigger in a drop target with a drag-over
 *   visual state (`[data-drop-target]`), keyboard/paste support via RAC's
 *   visually-hidden drop button, and the same trigger button as the
 *   always-available fallback path.
 *
 * The component is intentionally thin: it surfaces picked/dropped `File`
 * objects through `onSelectFiles` and does NO validation — `accept` is a
 * dialog filter only and drops bypass it, so consumers must validate.
 */
const _FileUpload = (
  props: FileUploadProps,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const {
    acceptedFileTypes,
    allowsMultiple = false,
    buttonText = "Browse files",
    children,
    className,
    dropText = "Drag and drop files here",
    excludeFromTabOrder = false,
    isDisabled = false,
    isLoading = false,
    mode = "button",
    onSelectFiles,
  } = props;

  const accept =
    acceptedFileTypes && acceptedFileTypes.length > 0
      ? acceptedFileTypes
      : undefined;

  const onTriggerSelect = useCallback(
    (fileList: FileList | null) => {
      const files = fileList == null ? [] : Array.from(fileList);

      if (files.length === 0) return;

      onSelectFiles?.(files, "trigger");
    },
    [onSelectFiles],
  );

  const onDrop = useCallback(
    async (event: DropEvent) => {
      const fileItems = event.items.filter(
        (item): item is FileDropItem => item.kind === "file",
      );

      if (fileItems.length === 0) return;

      const files = await Promise.all(
        fileItems.map(async (item) => item.getFile()),
      );

      onSelectFiles?.(files, "drop");
    },
    [onSelectFiles],
  );

  const trigger = (
    <FileTrigger
      acceptedFileTypes={accept}
      allowsMultiple={allowsMultiple}
      onSelect={onTriggerSelect}
    >
      <Button
        excludeFromTabOrder={excludeFromTabOrder}
        icon="upload"
        isDisabled={isDisabled}
        isLoading={isLoading}
        variant={mode === "dropzone" ? "outlined" : "filled"}
      >
        {buttonText}
      </Button>
    </FileTrigger>
  );

  if (mode === "button") {
    return (
      <div className={clsx(styles.fileUpload, className)} ref={ref}>
        {trigger}
      </div>
    );
  }

  return (
    <DropZone
      aria-label={props["aria-label"] ?? dropText}
      className={clsx(styles.fileUpload, styles.dropZone, className)}
      isDisabled={isDisabled}
      onDrop={onDrop}
      ref={ref}
    >
      <Icon name="cloud-upload" size="large" />
      <Text color="neutral" size="body">
        {dropText}
      </Text>
      {trigger}
      {children}
    </DropZone>
  );
};

export const FileUpload = forwardRef(_FileUpload);
